import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { XMarkIcon } from '@heroicons/react/24/outline'
import TableComponent from 'components/common/TableComponent'
import { formatDateTime, formatReset } from './format'

// "#12 · 9300ABCD · Box · 06:00:00 / -": a batch as the app or the server saw it.
const formatBatch = (id, prefix, type, resetTime, resetEvery) => (id
  ? `#${id} · ${prefix || '-'} · ${type || '-'} · ${formatReset(resetTime, resetEvery)}`
  : '-')

const flagCell = value => (value
  ? <span className='font-semibold text-amber-700'>✔</span>
  : '')

const RejectionsTable = ({ page, prefixFilter, loading, onClearPrefix, onPageChange }) => {
  const { t } = useTranslation()
  const { total = 0, limit = 0, offset = 0, rejections = [] } = page || {}
  const from = total ? offset + 1 : 0
  const to = Math.min(offset + limit, total)

  const tableData = {
    columns: [
      { key: 'createdAt', labelKey: 'system.scanRejections.cols.createdAt', orderBy: 'desc', cell: formatDateTime },
      { key: 'farmName', labelKey: 'system.scanRejections.cols.tenant' },
      { key: 'deviceIdentifier', labelKey: 'system.scanRejections.cols.device', cell: (value, row) => value || row.deviceId || '-' },
      { key: 'appVersion', labelKey: 'system.scanRejections.cols.appVersion' },
      { key: 'workerName', labelKey: 'system.scanRejections.cols.worker', cell: value => value || '-' },
      { key: 'source', labelKey: 'system.scanRejections.cols.source', cell: value => t(`system.scanRejections.sources.${value}`) },
      {
        key: 'reason',
        labelKey: 'system.scanRejections.cols.reason',
        cell: (value, row) => `${t(`system.scanRejections.reasons.${value}`)}${row.isRepeatRead ? ` (${t('system.scanRejections.repeatRead')})` : ''}`
      },
      { key: 'boxId', labelKey: 'system.scanRejections.cols.box' },
      { key: 'matchedBy', labelKey: 'system.scanRejections.cols.matchedBy' },
      {
        key: 'resolvedStickerId',
        labelKey: 'system.scanRejections.cols.resolvedBatch',
        cell: (value, row) => formatBatch(value, row.stickerPrefix, row.stickerType, row.resetTime, row.resetEvery)
      },
      {
        key: 'realStickerId',
        labelKey: 'system.scanRejections.cols.realBatch',
        cell: (value, row) => formatBatch(value, row.realPrefix, row.realStickerType, row.realResetTime, row.realResetEvery)
      },
      { key: 'batchMismatch', labelKey: 'system.scanRejections.cols.batchMismatch', cell: flagCell },
      { key: 'windowStart', labelKey: 'system.scanRejections.cols.windowStart', cell: formatDateTime },
      { key: 'windowBeforeDayStart', labelKey: 'system.scanRejections.cols.windowBeforeDayStart', cell: flagCell },
      { key: 'blockingAt', labelKey: 'system.scanRejections.cols.blockingAt', cell: formatDateTime },
      { key: 'blockingWorkerName', labelKey: 'system.scanRejections.cols.blockingWorker', cell: (value, row) => value || row.blockingWorkerId || '-' },
      { key: 'blockingAgeMinutes', labelKey: 'system.scanRejections.cols.blockingAge', cell: value => (value === null || value === undefined ? '-' : `${value} min`) },
      { key: 'blockedByPreviousDay', labelKey: 'system.scanRejections.cols.blockedByPreviousDay', cell: flagCell },
      { key: 'scannedDataFetchedAt', labelKey: 'system.scanRejections.cols.scannedDataFetchedAt', cell: formatDateTime },
      { key: 'tzOffsetMinutes', labelKey: 'system.scanRejections.cols.tzOffset' },
      { key: 'rawScan', labelKey: 'system.scanRejections.cols.rawScan' }
    ],
    rows: rejections
  }

  return (
    <div>
      <div className='flex flex-wrap items-center gap-3 mb-2'>
        <h4 className='text-base font-semibold text-greenPrimary'>{t('system.scanRejections.detailTitle')}</h4>
        {prefixFilter && (
          <button
            type='button'
            onClick={onClearPrefix}
            className='flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-sm text-amber-800 hover:bg-amber-200'
          >
            {t('system.scanRejections.prefixFilter', { prefix: prefixFilter })}
            <XMarkIcon className='w-4 h-4' />
          </button>
        )}
        <span className='ml-auto text-sm text-gray-500'>
          {t('system.scanRejections.pageRange', { from, to, total })}
        </span>
        <button
          type='button'
          disabled={loading || offset === 0}
          onClick={() => onPageChange(Math.max(offset - limit, 0))}
          className='px-3 py-1 rounded bg-gray-100 text-greenPrimary hover:bg-gray-200 disabled:opacity-50'
        >
          {t('system.scanRejections.previous')}
        </button>
        <button
          type='button'
          disabled={loading || to >= total}
          onClick={() => onPageChange(offset + limit)}
          className='px-3 py-1 rounded bg-gray-100 text-greenPrimary hover:bg-gray-200 disabled:opacity-50'
        >
          {t('system.scanRejections.next')}
        </button>
      </div>
      <TableComponent
        tableData={tableData}
        defaultSortColumn='createdAt'
        defaultSortOrder='desc'
        exportFileName='scan-rejections'
        exportTitle={t('system.scanRejections.detailTitle')}
      />
    </div>
  )
}

RejectionsTable.propTypes = {
  page: PropTypes.shape({
    total: PropTypes.number,
    limit: PropTypes.number,
    offset: PropTypes.number,
    rejections: PropTypes.arrayOf(PropTypes.object)
  }),
  prefixFilter: PropTypes.string,
  loading: PropTypes.bool,
  onClearPrefix: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired
}

export default RejectionsTable
